import { useState } from "react";
import { Section, NumField, ResultRow, PrimaryButton, SecondaryButton, SaveButton, CalcCard } from "@/components/CalcUI";
import { localCalc } from "@/lib/calc";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

let nextKey = 0;
const withKey = (s) => ({ ...s, _key: ++nextKey });
const stripKey = ({ _key, ...s }) => s;

const DEFAULT_SECTIONS = [
  { length_ft: "3000", inclination_deg: "0", friction_factor: "0.25" },
  { length_ft: "2000", inclination_deg: "45", friction_factor: "0.30" },
  { length_ft: "5000", inclination_deg: "88", friction_factor: "0.35" },
];

export default function TorqueDrag() {
  const [pipe, setPipe] = useState({ od_in: "5.5", wt_ppf: "20", mw: "10.0" });
  const [sections, setSections] = useState(() => DEFAULT_SECTIONS.map(withKey));
  const [out, setOut] = useState(null);

  const addSection = () => setSections([...sections, withKey({ length_ft: "1000", inclination_deg: "0", friction_factor: "0.25" })]);
  const removeSection = (i) => setSections(sections.filter((_, idx) => idx !== i));
  const updateSection = (i, k, v) => setSections(sections.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  const run = () => {
    try {
      const r = localCalc("torque_drag", {
        od_in: parseFloat(pipe.od_in),
        wt_ppf: parseFloat(pipe.wt_ppf),
        mw: parseFloat(pipe.mw),
        sections: sections.map((s) => ({
          length_ft: parseFloat(s.length_ft),
          inclination_deg: parseFloat(s.inclination_deg),
          friction_factor: parseFloat(s.friction_factor),
        })),
      });
      setOut(r);
      if (r.error) toast.error(r.error);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };

  return (
    <div data-testid="torque-drag-page">
      <Section
        title="Torque & Drag (Soft-String)"
        subtitle="Predict hookload for running-in / pulling-out / rotating and total torque before running casing. Model sections bottom-up (bit at bottom, surface at top) with inclination and friction factor for each interval."
      >
        <CalcCard testid="td-pipe-card" title="Pipe / Fluid">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <NumField testid="td-od" label="Casing OD" unit="in" value={pipe.od_in} onChange={(v) => setPipe({ ...pipe, od_in: v })} />
            <NumField testid="td-wt" label="Weight" unit="lb/ft" value={pipe.wt_ppf} onChange={(v) => setPipe({ ...pipe, wt_ppf: v })} />
            <NumField testid="td-mw" label="Mud weight" unit="ppg" value={pipe.mw} onChange={(v) => setPipe({ ...pipe, mw: v })} />
          </div>
        </CalcCard>

        <div className="mt-5 border border-[#334155] bg-[#11151c]">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#334155]">
            <h3 className="heading text-sm font-bold uppercase tracking-wide">Sections (bottom → top)</h3>
            <button
              data-testid="td-add-section"
              onClick={addSection}
              className="inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] px-2 py-1 mono text-xs uppercase tracking-widest"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full mono text-sm">
              <thead>
                <tr className="border-b border-[#334155] bg-[#0b0e13] text-xs uppercase tracking-widest text-[#94a3b8]">
                  <th className="text-left px-4 py-2">#</th>
                  <th className="text-left px-4 py-2">Length (ft)</th>
                  <th className="text-left px-4 py-2">Inclination (°)</th>
                  <th className="text-left px-4 py-2">μ friction</th>
                  <th className="px-4 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {sections.map((s, i) => (
                  <tr key={s._key} className={i % 2 === 0 ? "bg-[#0b0e13]" : "bg-[#11151c]"}>
                    <td className="px-4 py-2 text-[#f59e0b]">{i + 1}</td>
                    {["length_ft", "inclination_deg", "friction_factor"].map((k) => (
                      <td key={k} className="px-2 py-2">
                        <input
                          data-testid={`td-${k}-${i}`}
                          type="number"
                          value={s[k]}
                          onChange={(e) => updateSection(i, k, e.target.value)}
                          className="w-full bg-[#0b0e13] border border-[#334155] focus:border-[#f59e0b] focus:outline-none px-2 py-1 mono text-sm"
                        />
                      </td>
                    ))}
                    <td className="px-2 py-2 text-right">
                      {sections.length > 1 && (
                        <button
                          data-testid={`td-remove-${i}`}
                          onClick={() => removeSection(i)}
                          className="text-[#94a3b8] hover:text-[#ef4444]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-5 flex justify-between items-center gap-2">
          <PrimaryButton testid="td-run" onClick={run}>Compute Hookload</PrimaryButton>
          <SaveButton
            getPayload={() =>
              out && {
                category: "Torque & Drag",
                calculator: "torque_drag",
                inputs: { ...pipe, sections: sections.map(stripKey) },
                outputs: out,
                units: "field",
                default_name: "T&D Prediction",
              }
            }
          />
        </div>

        {out && !out.error && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="border border-[#334155] bg-[#11151c] p-5">
              <h4 className="heading text-xs uppercase tracking-widest text-[#f59e0b] mb-3">Hookload Envelope</h4>
              <ResultRow label="POOH (pulling out)" value={out.hookload_pooh_lbf?.toLocaleString()} unit="lbf" emphasis />
              <ResultRow label="Rotating (no axial drag)" value={out.hookload_rotating_lbf?.toLocaleString()} unit="lbf" />
              <ResultRow label="RIH (running in)" value={out.hookload_rih_lbf?.toLocaleString()} unit="lbf" />
              <ResultRow label="Total torque" value={out.total_torque_ftlb?.toLocaleString()} unit="ft·lb" emphasis />
              <ResultRow label="Buoyancy factor" value={out.buoyancy_factor} unit="—" />
            </div>
            <div className="border border-[#334155] bg-[#11151c] p-5">
              <h4 className="heading text-xs uppercase tracking-widest text-[#f59e0b] mb-3">Section Profile</h4>
              <div className="overflow-x-auto">
                <table className="w-full mono text-xs">
                  <thead>
                    <tr className="text-[#94a3b8] uppercase tracking-widest border-b border-[#334155]">
                      <th className="text-left py-1">§</th>
                      <th className="text-right py-1">L (ft)</th>
                      <th className="text-right py-1">α</th>
                      <th className="text-right py-1">W_b</th>
                      <th className="text-right py-1">F_n</th>
                      <th className="text-right py-1">Drag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {out.profile.map((p) => (
                      <tr key={p.section} className="border-b border-[#334155]/50">
                        <td className="py-1 text-[#f59e0b]">{p.section}</td>
                        <td className="py-1 text-right text-[#f8fafc]">{p.length_ft}</td>
                        <td className="py-1 text-right text-[#f8fafc]">{p.inclination_deg}°</td>
                        <td className="py-1 text-right text-[#94a3b8]">{p.buoyed_weight_lbf.toLocaleString()}</td>
                        <td className="py-1 text-right text-[#94a3b8]">{p.normal_force_lbf.toLocaleString()}</td>
                        <td className="py-1 text-right text-[#94a3b8]">{p.section_drag_lbf.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] mono text-[#64748b] mt-3 leading-relaxed">
                Numbered bottom → top. RIH assumes drag opposes running (subtract), POOH adds drag, rotating removes axial-drag but retains torque.
              </div>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
